import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Output
} from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';

import { BehaviorSubject, NEVER } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, shareReplay, take, takeUntil } from 'rxjs/operators';

import { ActionItem, ActionItemModifier, ActionItemStyle, BaseContainer, IconSize } from '@stylehaven/components';
import { isEqual } from 'lodash-es';
import { ParseScanItem } from '../../../helpers';
import { ApiFragment } from '../../../record/entities/api-fragment.entity';
import { ScanItemValidationOptions } from '../../entities/scan-item-validation-options.entity';
import { ScanItem, ScanItemState } from '../../entities/scan-item.entity';
import { ScanItemValidationService } from '../../services/scan-item-validation.service';
import { ScanSandbox } from '../../services/scan.sandbox';

@Component({
    standalone: false,
    selector: 'digihaven-record-scanner',
    templateUrl: './record-scanner.container.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class RecordScannerContainer extends BaseContainer implements OnInit, OnDestroy {
    @Input() id?: string;
    @Input() label?: string;
    @Input() recordControl!: UntypedFormControl;
    @Input() validationOptions: ScanItemValidationOptions;
    scanAction$: BehaviorSubject<ActionItem | null> = new BehaviorSubject<ActionItem | null>(null);

    @Output() scannedRecord: EventEmitter<ApiFragment | null> = new EventEmitter();
    @Output() invalidRecordScanned: EventEmitter<void> = new EventEmitter();

    private scanItemValidationService: ScanItemValidationService = inject(ScanItemValidationService);

    constructor(private readonly sandbox: ScanSandbox) {
        super();
    }

    ngOnInit(): void {
        this.initForm();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.scannedRecord.complete();
        this.invalidRecordScanned.complete();
    }

    private initForm() {
        this.recordControl = new UntypedFormControl(null, [Validators.required]);
        this.updateScanActionItem(false);

        this.recordControl.valueChanges
            .pipe(distinctUntilChanged(isEqual), debounceTime(100), takeUntil(this.destroy$))
            .subscribe(value => {
                if (value && this.scanAction$.value?.disabled === true) {
                    this.updateScanActionItem(false);
                }
                this.scannedRecord.next(null);
            });
        this.recordControl.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(status => {
            if (status === 'INVALID') {
                this.updateScanActionItem(false);
            }
        });
    }

    private updateScanActionItem(disabled: boolean) {
        this.scanAction$.next({
            label: this.translateService.instant('components.scanner-input.scan-action.label'),
            style: ActionItemStyle.Primary,
            modifiers: [ActionItemModifier.ActionInInputField],
            eventListener: () => this.handleRecordScan(),
            disabled: disabled,
            disabledInfoMsg: this.translateService.instant('components.scanner-input.scan-action.disabled-tooltip')
        });
    }

    handleRecordScan(): void {
        if (!this.recordControl.value) {
            return;
        }
        const rawValue = ParseScanItem(this.recordControl.value).id;
        this.recordControl.setValue(rawValue);
        this.recordControl.disable();
        if (rawValue !== undefined) {
            this.sandbox
                .getRecordById(rawValue)
                .pipe(
                    take(1),
                    takeUntil(this.destroy$),
                    catchError(err => {
                        console.error(err);
                        return this.handleRecordError();
                    }),
                    shareReplay({ bufferSize: 1, refCount: true })
                )
                .subscribe(record => this.handleRecordScanned(record));
        }
    }

    private handleRecordScanned(record: ApiFragment) {
        const scanItem: ScanItem = {
            id: record.getRecordId(),
            state: ScanItemState.Loading,
            record: record
        };
        const validatedItem = this.scanItemValidationService.validate(scanItem, this.validationOptions);
        this.recordControl.enable();
        if (validatedItem.state === ScanItemState.Accepted) {
            this.recordControl.setErrors(null);
            this.scannedRecord.next(record);
            this.updateScanActionItem(true);
        } else {
            this.recordControl.setErrors({ message: validatedItem.errorMsg });
            this.invalidRecordScanned.next();
            this.updateScanActionItem(false);
        }
    }

    private handleRecordError() {
        this.recordControl.enable();
        this.recordControl.setErrors({
            message: this.translateService.instant('components.scanner-input.not-found')
        });
        this.invalidRecordScanned.next();
        return NEVER;
    }
}
